import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import acornSrc from "@/assets/brand/acorn.png";

/**
 * AgeGate — overlay 18+ mostrato al primo accesso pubblico.
 *
 * Regole:
 *  - Persistenza in localStorage (`co_age_verified=true`) → una sola volta per device.
 *  - Bypass automatico se l'utente è già autenticato come `admin`/`staff`.
 *  - Blocca l'interazione con qualsiasi contenuto (fixed inset-0 z-[9999]).
 *  - Nessun tracker, nessuna telemetria — è solo un gate UX.
 */
const STORAGE_KEY = "co_age_verified";

export default function AgeGate() {
    const { user } = useAuth();
    const [verified, setVerified] = useState(() => {
        try {
            return localStorage.getItem(STORAGE_KEY) === "true";
        } catch {
            return false;
        }
    });

    // Bypass per admin/staff loggati (utenti di servizio).
    const bypass = user && (user.role === "admin" || user.role === "staff");

    useEffect(() => {
        if (bypass && !verified) {
            try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
            setVerified(true);
        }
    }, [bypass, verified]);

    // Impedisci scroll del body mentre il gate è visibile.
    useEffect(() => {
        if (verified || bypass) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [verified, bypass]);

    if (verified || bypass) return null;

    const accept = () => {
        try { localStorage.setItem(STORAGE_KEY, "true"); } catch {}
        setVerified(true);
    };

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#F5EFE7]"
            data-testid="age-gate-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="age-gate-title"
        >
            {/* Trama sottile per profondità */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.04]"
                style={{
                    backgroundImage:
                        "radial-gradient(circle at 20% 30%, #5C0F1F 0px, transparent 40%), radial-gradient(circle at 80% 70%, #8B0033 0px, transparent 50%)",
                }}
            />
            <div className="relative max-w-md w-[92%] sm:w-full mx-auto px-6 sm:px-10 py-10 sm:py-14 text-center">
                <img
                    src={acornSrc}
                    alt=""
                    aria-hidden="true"
                    className="h-14 sm:h-16 mx-auto mb-6"
                    style={{ opacity: 0.9 }}
                />
                <div
                    className="text-3xl sm:text-4xl text-[#212121] mb-2"
                    style={{
                        fontFamily:
                            "'Futura', 'Futura PT', 'Futura Std', 'Century Gothic', 'Trebuchet MS', 'Nunito Sans', sans-serif",
                        fontWeight: 300,
                        letterSpacing: "0.02em",
                        textTransform: "lowercase",
                    }}
                    data-testid="age-gate-brand"
                >
                    orterie
                </div>
                <div className="co-caption mb-8">Casa Orterie · Carta dei Vini</div>

                <h1
                    id="age-gate-title"
                    className="text-2xl sm:text-3xl leading-snug text-[hsl(var(--foreground))] mb-3"
                    style={{ fontFamily: "'Cormorant Garamond', 'EB Garamond', Georgia, serif" }}
                >
                    Prima di entrare
                </h1>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8 leading-relaxed max-w-sm mx-auto">
                    L'accesso alla nostra carta dei vini è riservato a chi ha compiuto la
                    maggiore età. Il consumo responsabile è parte del nostro stile.
                </p>

                <button
                    onClick={accept}
                    className="w-full co-btn-primary justify-center py-3.5 text-sm tracking-wide"
                    data-testid="age-gate-accept-btn"
                >
                    Ho più di 18 anni — Entra
                </button>

                <p className="mt-5 text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed">
                    Se non hai ancora 18 anni, ti chiediamo cortesemente di chiudere
                    questa pagina. Torna a trovarci quando sarà il momento giusto.
                </p>
            </div>
        </div>
    );
}
